import { useEffect, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ChevronDown, ExternalLink, Play, X } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getArtistPage, ARTIST_MENU_SECTIONS } from "@/lib/artistPages";

// Extract a YouTube video id from any common URL shape.
const ytIdFromUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.searchParams.get("v")) return u.searchParams.get("v");
      const m = u.pathname.match(/\/(embed|shorts|v)\/([\w-]{6,})/);
      if (m) return m[2];
    }
    return null;
  } catch {
    return null;
  }
};

// 60-second preview modal — supports YouTube (via iframe end= param) and direct
// audio URLs (via <audio> + setTimeout kill switch). Non-streamable platforms
// (Spotify/Apple/HyperFollow) are auto-detected and opened externally instead.
const PREVIEW_SECONDS = 60;

// URLs that can be inline-previewed. Everything else (Spotify/Apple/HyperFollow etc)
// blocks embedding, so we open those directly in a new tab rather than showing a
// broken audio element.
const isPreviewable = (url) => {
  if (!url) return false;
  const yt = ytIdFromUrl(url);
  if (yt) return true;
  try {
    const u = new URL(url);
    // Direct audio: our own upload endpoint or common audio file extensions.
    if (u.pathname.match(/\.(mp3|m4a|aac|wav|ogg|oga|flac)(\?.*)?$/i)) return true;
    if (u.pathname.includes("/api/uploads/audio/")) return true;
    return false;
  } catch {
    return false;
  }
};

const NewReleasePreviewModal = ({ preview, onClose }) => {
  const [remaining, setRemaining] = useState(PREVIEW_SECONDS);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!preview) return undefined;
    setRemaining(PREVIEW_SECONDS);
    const killAt = setTimeout(() => {
      try { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } } catch (_e) { /* noop */ }
      onClose();
    }, PREVIEW_SECONDS * 1000);
    const tick = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => { clearTimeout(killAt); clearInterval(tick); };
  }, [preview, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!preview) return null;
  const ytId = ytIdFromUrl(preview.url);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="new-release-preview-modal"
    >
      <div
        className="w-full max-w-xl border border-white/10 bg-[#0a0a0a] p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#A855F7]">
              60-Second Preview · {remaining.toString().padStart(2, "0")}s remaining
            </div>
            <div
              data-testid="new-release-preview-title"
              className="mt-1 truncate font-[Unbounded,sans-serif] text-lg font-bold text-[#FAFAFA]"
            >
              {preview.label || preview.url}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="new-release-preview-close"
            aria-label="Close preview"
            className="border border-white/10 p-2 text-[#FAFAFA] hover:bg-white hover:text-black"
          >
            <X size={16} />
          </button>
        </div>

        {ytId ? (
          <div className="relative aspect-video w-full bg-black">
            <iframe
              title="new-release-preview"
              className="h-full w-full"
              // enablejsapi + origin allow us to force playVideo() over postMessage
              // in browsers that suppress autoplay after a modal mount.
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&start=0&end=${PREVIEW_SECONDS}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              onLoad={(e) => {
                try {
                  e.currentTarget.contentWindow.postMessage(
                    JSON.stringify({ event: "command", func: "playVideo", args: [] }),
                    "*"
                  );
                } catch (_err) { /* iframe not ready — autoplay=1 will still try */ }
              }}
              data-testid="new-release-preview-iframe"
            />
          </div>
        ) : preview.url ? (
          <div className="border border-white/10 p-4">
            <audio
              ref={audioRef}
              autoPlay
              controls
              src={preview.url}
              className="w-full"
              data-testid="new-release-preview-audio"
            />
            <div className="mt-2 truncate font-mono text-xs font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
              {preview.url}
            </div>
          </div>
        ) : (
          <div className="font-mono text-sm font-bold text-[#FF3B30]">No URL provided.</div>
        )}

        <a
          href={preview.full_url || preview.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#A855F7]"
        >
          Open full track <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
};

const ArtistMenuBox = ({ section, links, onPreview }) => {
  const [open, setOpen] = useState(false);
  const hasLinks = Array.isArray(links) && links.length > 0;
  const isPreview = section.key === "new_releases";
  return (
    <div
      data-testid={`artist-menu-${section.key}`}
      className="border border-white/15 bg-black/55 backdrop-blur-md"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`artist-menu-panel-${section.key}`}
        data-testid={`artist-menu-toggle-${section.key}`}
        className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A855F7]"
      >
        <div className="min-w-0 flex-1">
          <div className="truncate font-[Unbounded,sans-serif] text-sm font-bold uppercase tracking-tight text-[#FAFAFA]">
            {section.label}
          </div>
          <div className="mt-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
            {hasLinks ? `${links.length} item${links.length === 1 ? "" : "s"}` : "Coming soon"}
          </div>
        </div>
        <ChevronDown
          size={14}
          className={`flex-shrink-0 text-[#A855F7] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open ? (
        <div
          id={`artist-menu-panel-${section.key}`}
          className="border-t border-white/10"
          data-testid={`artist-menu-panel-${section.key}`}
        >
          {hasLinks ? (
            <ul>
              {links.map((l, i) => (
                <li key={`${l.url}-${i}`} className="border-b border-white/5 last:border-b-0">
                  {isPreview && isPreviewable(l.url) ? (
                    <button
                      type="button"
                      onClick={() => onPreview && onPreview({ url: l.url, full_url: l.full_url, label: l.label, cover: l.cover })}
                      data-testid={`artist-menu-link-${section.key}-${i}`}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left font-mono text-xs font-bold text-[#FAFAFA] transition-colors hover:bg-[#A855F7]/15 hover:text-[#A855F7]"
                    >
                      {l.cover ? (
                        <img
                          src={l.cover}
                          alt=""
                          className="h-9 w-9 flex-shrink-0 border border-white/10 bg-black object-cover"
                          data-testid={`artist-menu-link-${section.key}-${i}-cover`}
                        />
                      ) : null}
                      <span className="min-w-0 flex-1 truncate">{l.label || l.url}</span>
                      <span className="flex flex-shrink-0 items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                        <Play size={10} fill="currentColor" /> 60s
                      </span>
                    </button>
                  ) : l.url || l.full_url ? (
                    <a
                      href={l.full_url || l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`artist-menu-link-${section.key}-${i}`}
                      className="flex items-center gap-3 px-3 py-2 font-mono text-xs font-bold text-[#FAFAFA] transition-colors hover:bg-[#A855F7]/15 hover:text-[#A855F7]"
                    >
                      {l.cover ? (
                        <img
                          src={l.cover}
                          alt=""
                          className="h-9 w-9 flex-shrink-0 border border-white/10 bg-black object-cover"
                          data-testid={`artist-menu-link-${section.key}-${i}-cover`}
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{l.label || l.url}</div>
                        {l.subtitle ? (
                          <div className="mt-0.5 truncate font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
                            {l.subtitle}
                          </div>
                        ) : null}
                      </div>
                      <ExternalLink size={12} className="flex-shrink-0 text-[#A1A1AA]" />
                    </a>
                  ) : (
                    // Static entry (announcement, no URL). Upcoming Release gets a
                    // larger "poster" layout with the cover displayed at menu width.
                    section.key === "upcoming" ? (
                      <div
                        data-testid={`artist-menu-link-${section.key}-${i}`}
                        className="flex flex-col"
                      >
                        {l.cover ? (
                          <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "1 / 1" }}>
                            <img
                              src={l.cover}
                              alt=""
                              className="h-full w-full object-cover"
                              data-testid={`artist-menu-link-${section.key}-${i}-cover`}
                            />
                          </div>
                        ) : null}
                        <div className="p-3">
                          <div className="whitespace-normal font-[Unbounded,sans-serif] text-sm font-bold uppercase leading-snug tracking-tight text-[#FAFAFA]">
                            {l.label}
                          </div>
                          {l.subtitle ? (
                            <div className="mt-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                              {l.subtitle}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <div
                        data-testid={`artist-menu-link-${section.key}-${i}`}
                        className="flex items-center gap-3 px-3 py-2 font-mono text-xs font-bold text-[#FAFAFA]"
                      >
                        {l.cover ? (
                          <img
                            src={l.cover}
                            alt=""
                            className="h-9 w-9 flex-shrink-0 border border-white/10 bg-black object-cover"
                            data-testid={`artist-menu-link-${section.key}-${i}-cover`}
                          />
                        ) : null}
                        <div className="min-w-0 flex-1">
                          <div className="whitespace-normal leading-snug">{l.label}</div>
                          {l.subtitle ? (
                            <div className="mt-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                              {l.subtitle}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-3 text-center font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
              No entries yet — check back soon.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const ArtistDetail = () => {
  const { slug } = useParams();
  const artist = getArtistPage(slug);
  const [settings, setSettings] = useState({});
  const [preview, setPreview] = useState(null);
  const [videoVisible, setVideoVisible] = useState(true);
  const bgVideoRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Ensure we always land at the top of the artist page (deep-linked or clicked
  // from a scrolled homepage tile).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Optional background-video autoplay delay: HIDE the video and show the backdrop
  // image alone for N ms, then fade the video in and start it.
  useEffect(() => {
    if (!artist || artist.background?.type !== "video") {
      setVideoVisible(true);
      return undefined;
    }
    const delay = artist.background.autoplayDelayMs || 0;
    if (delay <= 0) { setVideoVisible(true); return undefined; }
    setVideoVisible(false);
    const id = setTimeout(() => {
      setVideoVisible(true);
      const v = bgVideoRef.current;
      if (v) v.play().catch(() => { /* browser may block silent autoplay */ });
    }, delay);
    return () => clearTimeout(id);
  }, [artist]);

  if (!artist) return <Navigate to="/" replace />;
  const menu = artist.menu || {};

  return (
    <div className="relative min-h-screen overflow-hidden text-[#FAFAFA]" data-testid="artist-detail-root">
      {/* Background layer — image OR video, full-bleed, dimmed for legibility */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-black">
        {artist.background.backdrop ? (
          <img
            src={artist.background.backdrop}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="artist-bg-backdrop"
          />
        ) : null}
        {artist.background.type === "video" ? (
          <video
            ref={bgVideoRef}
            className={`relative h-full w-full transition-opacity duration-700 ${artist.background.fit === "contain" ? "object-contain" : "object-cover"} ${videoVisible ? "opacity-100" : "opacity-0"}`}
            autoPlay={!artist.background.autoplayDelayMs}
            muted
            loop
            playsInline
            preload="auto"
            data-testid="artist-bg-video"
          >
            {(artist.background.sources || [{ src: artist.background.src, type: "video/mp4" }]).map((s) => (
              <source key={s.src} src={s.src} type={s.type} />
            ))}
          </video>
        ) : (
          <img
            src={artist.background.src}
            alt=""
            className={`relative h-full w-full ${artist.background.fit === "contain" ? "object-contain" : "object-cover"}`}
            data-testid="artist-bg-image"
          />
        )}
        {/* Dim + gradient tint so foreground copy stays readable over any image/frame */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%)",
          }}
        />
      </div>

      <Navbar />

      <section className="relative">
        <div className="mx-auto max-w-[1400px] px-4 pb-32 pt-10 sm:px-6 sm:pt-16 md:pt-24">
          <Link
            to="/#featured"
            data-testid="artist-back-link"
            className="inline-flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-2 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#FAFAFA] backdrop-blur-sm transition-colors hover:bg-white hover:text-black"
          >
            <ArrowLeft size={14} /> Featured
          </Link>

          <div className="mt-10 grid grid-cols-1 gap-10 md:mt-14 md:grid-cols-12 md:gap-14">
            <div className="md:col-span-7">
              <div className="font-mono text-sm font-bold uppercase tracking-[0.28em] text-[#A855F7]">
                Black Box Music · Featured Artist
              </div>
              <h1
                data-testid="artist-name"
                className="mt-4 font-[Unbounded,sans-serif] text-5xl font-bold uppercase leading-[0.95] tracking-tight text-[#FAFAFA] sm:text-6xl lg:text-7xl"
              >
                {artist.name}
              </h1>
              <p className="mt-6 max-w-xl font-mono text-lg font-bold leading-relaxed text-[#FAFAFA]">
                {artist.tagline}
              </p>
              {artist.synopsis ? (
                <p
                  data-testid="artist-synopsis"
                  className="mt-5 max-w-xl text-base font-bold leading-relaxed text-white sm:text-lg"
                >
                  {artist.synopsis}
                </p>
              ) : null}

              <div className="mt-10 flex flex-col gap-2 sm:max-w-xs" data-testid="artist-menu-list">
                {ARTIST_MENU_SECTIONS.map((section) => (
                  <ArtistMenuBox
                    key={section.key}
                    section={section}
                    links={menu[section.key] || []}
                    onPreview={(item) => setPreview(item)}
                  />
                ))}
              </div>
            </div>

            {(() => {
              // Backward-compat: accept either { spotlight: {...} } or { spotlights: [...] }.
              const items = Array.isArray(artist.spotlights)
                ? artist.spotlights
                : (artist.spotlight ? [artist.spotlight] : []);
              if (items.length === 0) return null;
              return (
                <div className="flex flex-col items-end gap-4 md:col-span-5 lg:col-span-4 lg:col-start-9">
                  {items.map((sp, i) => (
                    <a
                      key={`${sp.url}-${i}`}
                      href={sp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`artist-spotlight-card-${i}`}
                      className="group block w-full max-w-[280px] border border-white/15 bg-black/55 backdrop-blur-md transition-colors hover:border-[#A855F7]/70 hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A855F7]"
                      aria-label={`Open ${sp.title} on the release platform`}
                    >
                      <div className="border-b border-white/10 bg-black/40 px-3 py-1.5">
                        <div className="font-mono text-[9px] font-bold uppercase tracking-[0.28em] text-[#A855F7]">
                          {sp.subtitle || "Featured Release"}
                        </div>
                      </div>
                      <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "1 / 1" }}>
                        <img
                          src={sp.cover}
                          alt={`${sp.title} artwork`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                          data-testid={`artist-spotlight-cover-${i}`}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5 p-3">
                        <div
                          data-testid={`artist-spotlight-title-${i}`}
                          className="font-[Unbounded,sans-serif] text-base font-bold uppercase leading-tight tracking-tight text-[#FAFAFA] transition-colors group-hover:text-[#A855F7]"
                        >
                          {sp.title}
                        </div>
                        <div className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#FAFAFA]">
                          {sp.artist}
                        </div>
                        <div className="mt-2.5 inline-flex items-center justify-center gap-1.5 border border-[#A855F7] bg-[#A855F7]/10 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-[#A855F7] transition-colors group-hover:bg-[#A855F7] group-hover:text-black">
                          {sp.cta || "Listen Now"} <ExternalLink size={11} />
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      <Footer settings={settings} />
      <NewReleasePreviewModal preview={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export default ArtistDetail;
